import { getAnalyticsWebClient } from '@confluence/analytics-web-client';
import { getBrowserBridgeAPI, client } from '@atlassian/rovo-content-bridge-api';
import {
	ConfluencePageCreateCommand,
	ConfluenceUpdatePageCommand,
	ConfluenceWhiteboardCreateCommand,
	ConfluenceWhiteboardUpdateCommand,
	getConfluenceEditorClientId,
	CONFLUENCE_WHITEBOARD_CLIENT_ID,
} from '@atlassian/rovo-content-bridge-api-commands';

declare const global: any;

// Define the desktop bridge client ID
const DESKTOP_BRIDGE_CLIENT_ID = client('desktop-bridge');

export const isDesktop = (): boolean => {
	return !!global?.window?.desktopNativeBridge;
};

type IPCListener = (...args: any[]) => () => void;
export const establishIPCListeners = (): (() => void) => {
	if (!isDesktop()) {
		return () => {};
	}

	const listenersCleanup: (() => void)[] = [];

	listenersCleanup.push(
		(global.window.desktopNativeBridge.onAnalyticsEvent as IPCListener)(
			(eventData: Record<string, any>) => {
				getAnalyticsWebClient()
					.then((client) => {
						if (eventData.type === 'sendTrackEvent' && eventData.data) {
							client.sendTrackEvent(eventData.data);
						}
					})
					.catch(() => {});
			},
		),
	);

	// Rovo Bridge API IPC listener
	if (global.window.desktopNativeBridge.onRovoBridgeEvent) {
		listenersCleanup.push(
			(global.window.desktopNativeBridge.onRovoBridgeEvent as IPCListener)(
				(eventData: Record<string, any>) => {
					const bridgeAPI = getBrowserBridgeAPI();
					if (eventData.type === 'ready') {
						bridgeAPI.ready(DESKTOP_BRIDGE_CLIENT_ID);
					} else if (eventData.type === 'send' && eventData) {
						if (eventData.data.params) {
							const message = eventData.data.params;
							try {
								if (message.key === 'aifc-confluence-page-create') {
									const commandParams = {
										...message,
										senderId: DESKTOP_BRIDGE_CLIENT_ID,
										receiverId: getConfluenceEditorClientId(message.data.contentId),
									};
									bridgeAPI.send(ConfluencePageCreateCommand, commandParams);
								}
								// Check if this is a page update message
								else if (
									message.key === 'aifc-confluence-page-update' ||
									message.key === 'aifc-adf-update'
								) {
									const commandParams = {
										...message,
										senderId: DESKTOP_BRIDGE_CLIENT_ID,
										receiverId: getConfluenceEditorClientId(message.data.contentId),
									};

									bridgeAPI.send(ConfluenceUpdatePageCommand, commandParams);
								} else if (message.key === 'aifc-confluence-whiteboard-create' || message.key === 'aifc-confluence-whiteboard-diagramming') {
									const commandParams = {
										...message,
										senderId: DESKTOP_BRIDGE_CLIENT_ID,
										receiverId: CONFLUENCE_WHITEBOARD_CLIENT_ID,
									};
									bridgeAPI.send(ConfluenceWhiteboardCreateCommand, commandParams);
								} else if (message.key === 'aifc-confluence-whiteboard-update') {
									const commandParams = {
										...message,
										senderId: DESKTOP_BRIDGE_CLIENT_ID,
										receiverId: CONFLUENCE_WHITEBOARD_CLIENT_ID,
									};
									bridgeAPI.send(ConfluenceWhiteboardUpdateCommand, commandParams);
								}
							} catch (error) {}
						} else {
							// Fallback to original behavior for non-streaming messages
							bridgeAPI.send(eventData.data.command, eventData.data.params);
						}
					} else if (eventData.type === 'on' && eventData.data) {
						bridgeAPI.on(eventData.data.clientId, eventData.data.command, eventData.data.handler);
					} else if (eventData.type === 'off' && eventData.data) {
						bridgeAPI.off(eventData.data.clientId, eventData.data.command);
					} else if (eventData.type === 'teardown' && eventData.data) {
						bridgeAPI.teardown(eventData.data.clientId);
					}
				},
			),
		);
	}

	return () => {
		listenersCleanup.forEach((cleanUpFn) => cleanUpFn());
	};
};
